import axios from 'axios';
import errorMessages from './errorMessages.json';
import { API_URL } from './runtimeConfig';

const apiUrl = API_URL;

const axiosInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let getAccessToken = () => null;
let shouldAttemptRefresh = () => false;
let refreshAccessToken = null;
let onAuthFailure = () => {};

let refreshPromise = null;

const isAuthEndpoint = (url = '') => {
  const normalized = String(url);
  return (
    normalized.includes('/auth/login') ||
    normalized.includes('/auth/register') ||
    normalized.includes('/auth/refresh') ||
    normalized.includes('/auth/logout')
  );
};

export const configureAxiosAuth = ({
  getAccessTokenFn,
  shouldAttemptRefreshFn,
  refreshAccessTokenFn,
  onAuthFailureFn,
}) => {
  getAccessToken = typeof getAccessTokenFn === 'function' ? getAccessTokenFn : () => null;
  shouldAttemptRefresh = typeof shouldAttemptRefreshFn === 'function' ? shouldAttemptRefreshFn : () => false;
  refreshAccessToken = typeof refreshAccessTokenFn === 'function' ? refreshAccessTokenFn : null;
  onAuthFailure = typeof onAuthFailureFn === 'function' ? onAuthFailureFn : () => {};
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken?.();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status && errorMessages[error.response.status] && !error?.response?.data?.message) {
      error.userMessage = errorMessages[error.response.status];
    }

    const originalRequest = error?.config;
    const isUnauthorized = error?.response?.status === 401;

    if (!isUnauthorized || !originalRequest || originalRequest._retry || isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    if (!refreshAccessToken || !shouldAttemptRefresh()) {
      onAuthFailure(error);
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      const refreshedToken = getAccessToken?.();
      if (refreshedToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
      }
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      onAuthFailure(refreshError);
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
